import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

function fmt(v: any, suffix = '') {
  if (v === null || v === undefined || v === 0 || v === '0') return '—';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString('tr-TR') + (suffix ? ' ' + suffix : '');
}

// ── Rapor grupları ──────────────────────────────────────────────────────────

const GRUPLAR = [
  {
    id: 'fiziksel', ad: 'Fiziksel Yapı', ikon: '🏗️',
    raporlar: [
      { id: 'arazi',   ad: 'Arazi Varlığı',       ikon: '🌱', yilSayisi: 1 },
      { id: 'sulama',  ad: 'Sulama Sistemleri',    ikon: '💧', yilSayisi: 1 },
      { id: 'tesis',  ad: 'Tarımsal Tesisler-1 (Kapasite)',  ikon: '🏭', yilSayisi: 1 },
      { id: 'tesis2', ad: 'Tarımsal Tesisler-2 (Depolama)', ikon: '🏭', yilSayisi: 1 },
      { id: 'traktor', ad: 'Traktör Varlığı',      ikon: '🚜', yilSayisi: 2 },
      { id: 'tasit',   ad: 'Motorlu Araç Varlığı', ikon: '🚗', yilSayisi: 1 },
    ],
  },
  {
    id: 'hayvancilik', ad: 'Hayvancılık', ikon: '🐄',
    raporlar: [
      { id: 'buyukbas',     ad: 'Büyükbaş Hayvan Varlığı',       ikon: '🐄', yilSayisi: 1 },
      { id: 'buyukbas-irk', ad: 'Büyükbaş — Irk Bazlı Dökümü',   ikon: '🐄', yilSayisi: 1 },
      { id: 'kucukbas',     ad: 'Küçükbaş Hayvan Varlığı',       ikon: '🐑', yilSayisi: 1 },
    ],
  },
  {
    id: 'bitkisel', ad: 'Bitkisel Üretim', ikon: '🌾',
    raporlar: [
      { id: 'tarla',           ad: 'Tarla Bitkileri (İşletme Özeti)', ikon: '🌾', yilSayisi: 1 },
      { id: 'tarla-urun',      ad: 'Tarla Bitkileri (Ürün Bazlı)',    ikon: '🌾', yilSayisi: 1 },
      { id: 'tarla-bugday',    ad: 'Buğday Üretimi',                  ikon: '🌾', yilSayisi: 1, urun: 'Buğday' },
      { id: 'tarla-arpa',      ad: 'Arpa Üretimi',                    ikon: '🌾', yilSayisi: 1, urun: 'Arpa' },
      { id: 'tarla-tritikale', ad: 'Tritikale Üretimi',               ikon: '🌾', yilSayisi: 1, urun: 'Tritikale' },
      { id: 'tarla-yulaf',     ad: 'Yulaf Üretimi',                   ikon: '🌾', yilSayisi: 1, urun: 'Yulaf' },
      { id: 'tarla-misir',     ad: 'Mısır Üretimi',                   ikon: '🌽', yilSayisi: 1, urun: 'Mısır' },
      { id: 'tarla-aycicegi',  ad: 'Ayçiçeği Üretimi',                ikon: '🌻', yilSayisi: 1, urun: 'Ayçiçeği' },
      { id: 'tarla-mercimek',  ad: 'Mercimek Üretimi',                ikon: '🫘', yilSayisi: 1, urun: 'Mercimek' },
      { id: 'tarla-nohut',     ad: 'Nohut Üretimi',                   ikon: '🫘', yilSayisi: 1, urun: 'Nohut' },
      { id: 'yem',             ad: 'Yem Bitkileri Üretimi',           ikon: '🌿', yilSayisi: 1 },
    ],
  },
];

// ── Tablo bileşenleri ───────────────────────────────────────────────────────

const TH: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <th style={{
    padding: '7px 10px', background: '#1a4f6e', color: '#fff',
    fontWeight: 600, fontSize: 12, textAlign: 'center',
    borderRight: '1px solid #2a6090', whiteSpace: 'nowrap',
  }} className={className}>{children}</th>
);

const TD: React.FC<{ children: React.ReactNode; bold?: boolean; right?: boolean }> = ({ children, bold, right }) => (
  <td style={{
    padding: '6px 10px', fontSize: 13, borderBottom: '1px solid #eee',
    fontWeight: bold ? 700 : 400, textAlign: right ? 'right' : 'left',
    color: bold ? '#1a4f6e' : '#333',
  }}>{children}</td>
);

function THeader({ children }: { children: React.ReactNode }) {
  return (
    <thead>
      <tr style={{ background: '#1a4f6e' }}>{children}</tr>
    </thead>
  );
}

function TRow({ row, cols }: { row: any; cols: { key: string; suffix?: string; right?: boolean }[] }) {
  const isTop = row._toplam;
  return (
    <tr style={{ background: isTop ? '#eaf1f8' : undefined }}>
      <TD bold={isTop}>{row.isletme}</TD>
      {cols.map(c => (
        <TD key={c.key} right={c.right !== false} bold={isTop}>
          {fmt(row[c.key], c.suffix || '')}
        </TD>
      ))}
    </tr>
  );
}

// ── Rapor tabloları ─────────────────────────────────────────────────────────

function AraziTablo({ satirlar }: { satirlar: any[] }) {
  const cols = [
    { key: 'toplam', suffix: 'da' }, { key: 'tarla', suffix: 'da' },
    { key: 'otlakiye', suffix: 'da' }, { key: 'bahce', suffix: 'da' },
    { key: 'kultur_disi', suffix: 'da' }, { key: 'sulanan', suffix: 'da' },
  ];
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <THeader>
        <TH>İşletme</TH><TH>Toplam Arazi (da)</TH><TH>Tarla (da)</TH>
        <TH>Otlakiye (da)</TH><TH>Bahçe (da)</TH><TH>Kültürdışı (da)</TH><TH>Sulanan (da)</TH>
      </THeader>
      <tbody>{satirlar.map((r: any, i: number) => <TRow key={i} row={r} cols={cols} />)}</tbody>
    </table>
  );
}

function AraziTablosu({ data }: { data: any }) {
  return (
    <div>
      <div style={{ padding: '10px 14px', background: '#eaf1f8', fontWeight: 700, fontSize: 13, color: '#1a4f6e', borderBottom: '2px solid #1a4f6e' }}>
        TİGEM Kullanımındaki İşletmeler (Faal + Merkez)
      </div>
      <AraziTablo satirlar={data.faal || []} />
      <div style={{ padding: '10px 14px', marginTop: 24, background: '#f5f0ea', fontWeight: 700, fontSize: 13, color: '#7a4a1e', borderBottom: '2px solid #c47a2a' }}>
        Uzun Süreli Kiralanan İşletmeler
      </div>
      <AraziTablo satirlar={data.kiralik || []} />
    </div>
  );
}

function SulamaTablosu({ data }: { data: any }) {
  const cols = [
    { key: 'center_pivot', suffix: 'da' }, { key: 'yagmurlama', suffix: 'da' },
    { key: 'damla', suffix: 'da' }, { key: 'toplam_debi', suffix: 'lt/sn' },
    { key: 'tarla_arazisi', suffix: 'da' }, { key: 'sulanan', suffix: 'da' },
    { key: 'sulama_orani', suffix: '%' },
  ];
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <THeader>
        <TH>İşletme</TH><TH>Center Pivot (da)</TH><TH>Yağmurlama (da)</TH>
        <TH>Damla (da)</TH><TH>Toplam Debi (lt/sn)</TH>
        <TH>Tarla Arazisi (da)</TH><TH>Sulanan (da)</TH><TH>Oran (%)</TH>
      </THeader>
      <tbody>{data.satirlar.map((r: any, i: number) => <TRow key={i} row={r} cols={cols} />)}</tbody>
    </table>
  );
}

function TesisTablosu({ data }: { data: any }) {
  const cols = [
    { key: 'tohum_hazirlama', suffix: 'ton/saat' }, { key: 'misir_kurutma', suffix: 'ton/saat' },
    { key: 'yem_hazirlama', suffix: 'ton/saat' }, { key: 'buyukbas_barinagi', suffix: 'baş' },
    { key: 'kucukbas_barinagi', suffix: 'baş' },
  ];
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <THeader>
        <TH>İşletme</TH><TH>Tohum Hazırlama (t/s)</TH><TH>Mısır Kurutma (t/s)</TH>
        <TH>Yem Hazırlama (t/s)</TH><TH>Büyükbaş Barınak (baş)</TH><TH>Küçükbaş Barınak (baş)</TH>
      </THeader>
      <tbody>{data.satirlar.map((r: any, i: number) => <TRow key={i} row={r} cols={cols} />)}</tbody>
    </table>
  );
}

function Tesis2Tablosu({ data }: { data: any }) {
  const cols = [
    { key: 'ambar_ton', suffix: 'ton' }, { key: 'akaryakit_m3', suffix: 'm³' },
    { key: 'silo_ton', suffix: 'ton' }, { key: 'hangar_m2', suffix: 'm²' },
  ];
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <THeader>
        <TH>İşletme</TH><TH>Ambar (ton)</TH><TH>Akaryakıt Tankı (m³)</TH>
        <TH>Silo (ton)</TH><TH>Hangar (m²)</TH>
      </THeader>
      <tbody>{data.satirlar.map((r: any, i: number) => <TRow key={i} row={r} cols={cols} />)}</tbody>
    </table>
  );
}

function TraktorTablosu({ data, yil1, yil2 }: { data: any; yil1: number; yil2: number }) {
  const cols = [
    { key: `adet_${yil1}`, suffix: 'ad' }, { key: `guc_${yil1}`, suffix: 'kW' },
    { key: `adet_${yil2}`, suffix: 'ad' }, { key: `guc_${yil2}`, suffix: 'kW' },
  ];
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#1a4f6e' }}>
          <th rowSpan={2} style={{ padding: '7px 10px', color: '#fff', fontSize: 12, fontWeight: 600, borderRight: '1px solid #2a6090', verticalAlign: 'middle' }}>İşletme</th>
          <th colSpan={2} style={{ padding: '7px 10px', color: '#fff', fontSize: 12, fontWeight: 600, borderRight: '1px solid #2a6090', textAlign: 'center', borderBottom: '1px solid #2a6090' }}>{yil1}</th>
          <th colSpan={2} style={{ padding: '7px 10px', color: '#fff', fontSize: 12, fontWeight: 600, textAlign: 'center', borderBottom: '1px solid #2a6090' }}>{yil2}</th>
        </tr>
        <tr style={{ background: '#1a4f6e' }}>
          <TH>Adet</TH><TH>Güç (kW)</TH><TH>Adet</TH><TH>Güç (kW)</TH>
        </tr>
      </thead>
      <tbody>{data.satirlar.map((r: any, i: number) => <TRow key={i} row={r} cols={cols} />)}</tbody>
    </table>
  );
}

function TasitTablosu({ data }: { data: any }) {
  const cols = [
    { key: 'hizmet_otosu' }, { key: 'hasta_nakil' }, { key: 'kamyon' },
    { key: 'minibus' }, { key: 'midibus_kamyonet' }, { key: 'cekici_treyler' },
    { key: 'itfaiye' }, { key: 'su_arazoz' }, { key: 'seyyar_atolye' },
    { key: 'akaryakit' }, { key: 'toplam' },
  ];
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <THeader>
        <TH>İşletme</TH><TH>Hizmet Otosu</TH><TH>Hasta Nakil</TH>
        <TH>Kamyon</TH><TH>Minibüs</TH><TH>Midibüs/Kmyt.</TH>
        <TH>Çekici-Treyler</TH><TH>İtfaiye</TH><TH>Su/Arazöz</TH>
        <TH>Seyyar Atölye</TH><TH>Akaryakıt</TH><TH>Toplam</TH>
      </THeader>
      <tbody>{data.satirlar.map((r: any, i: number) => <TRow key={i} row={r} cols={cols} />)}</tbody>
    </table>
  );
}

function BuyukbasTablosu({ data }: { data: any }) {
  const cols = [
    { key: 'boga', suffix: 'baş' }, { key: 'inek', suffix: 'baş' },
    { key: 'duve', suffix: 'baş' }, { key: 'dana', suffix: 'baş' },
    { key: 'buzagi', suffix: 'baş' }, { key: 'toplam', suffix: 'baş' },
  ];
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <THeader>
        <TH>İşletme</TH><TH>Boğa</TH><TH>İnek</TH>
        <TH>Düve</TH><TH>Dana</TH><TH>Buzağı</TH><TH>Toplam</TH>
      </THeader>
      <tbody>{data.satirlar.map((r: any, i: number) => <TRow key={i} row={r} cols={cols} />)}</tbody>
    </table>
  );
}

function BuyukbasIrkTablosu({ data }: { data: any }) {
  const cols = ['boga','inek','duve','dana','buzagi','toplam'];
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <THeader>
        <TH>İşletme</TH><TH>Irk</TH>
        <TH>Boğa</TH><TH>İnek</TH><TH>Düve</TH><TH>Dana</TH><TH>Buzağı</TH><TH>Toplam</TH>
      </THeader>
      <tbody>
        {data.satirlar.map((r: any, i: number) => {
          if (r._toplam) return (
            <tr key={i} style={{ background: '#eaf1f8' }}>
              <td colSpan={2} style={{ padding: '6px 10px', fontWeight: 700, fontSize: 13, color: '#1a4f6e' }}>{r.isletme}</td>
              {cols.map(c => <TD key={c} right bold>{fmt(r[c])}</TD>)}
            </tr>
          );
          if (r._isletme) return (
            <tr key={i} style={{ background: '#f0f4f8' }}>
              <td colSpan={8} style={{ padding: '6px 10px', fontWeight: 700, fontSize: 13, color: '#1a4f6e', borderTop: '2px solid #c8d8e8' }}>{r.isletme}</td>
            </tr>
          );
          if (r._isletme_top) return (
            <tr key={i} style={{ background: '#f8fafc' }}>
              <TD bold> </TD>
              <TD bold>↳ Toplam</TD>
              {cols.map(c => <TD key={c} right bold>{fmt(r[c])}</TD>)}
            </tr>
          );
          return (
            <tr key={i}>
              <TD> </TD>
              <td style={{ padding: '5px 10px 5px 22px', fontSize: 12, color: '#555', borderBottom: '1px solid #f0f0f0' }}>{r.irk}</td>
              {cols.map(c => <TD key={c} right>{fmt(r[c])}</TD>)}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function KucukbasTablosu({ data }: { data: any }) {
  const cols = [
    { key: 'koc_teke', suffix: 'baş' }, { key: 'ana', suffix: 'baş' },
    { key: 'genc', suffix: 'baş' }, { key: 'kuzu_oglak', suffix: 'baş' },
    { key: 'toplam', suffix: 'baş' },
  ];
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <THeader>
        <TH>İşletme</TH><TH>Koç/Teke</TH><TH>Ana Hayvan</TH>
        <TH>Genç</TH><TH>Kuzu/Oğlak</TH><TH>Toplam</TH>
      </THeader>
      <tbody>{data.satirlar.map((r: any, i: number) => <TRow key={i} row={r} cols={cols} />)}</tbody>
    </table>
  );
}

function TarlaBitkisiTablosu({ data }: { data: any }) {
  const cols = [
    { key: 'hasat_alani', suffix: 'da' }, { key: 'uretim_kg', suffix: 'kg' },
    { key: 'verim_kg_da', suffix: 'kg/da' },
  ];
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <THeader>
        <TH>İşletme</TH><TH>Hasat Alanı (da)</TH>
        <TH>Üretim (kg)</TH><TH>Ortalama Verim (kg/da)</TH>
      </THeader>
      <tbody>{data.satirlar.map((r: any, i: number) => <TRow key={i} row={r} cols={cols} />)}</tbody>
    </table>
  );
}

function TarlaUrunTablosu({ data }: { data: any }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <THeader>
        <TH>Ürün</TH><TH>İşletme</TH>
        <TH>Hasat Alanı (da)</TH><TH>Üretim (kg)</TH><TH>Verim (kg/da)</TH>
      </THeader>
      <tbody>
        {data.satirlar.map((r: any, i: number) => {
          if (r._toplam) return (
            <tr key={i} style={{ background: '#eaf1f8' }}>
              <td colSpan={2} style={{ padding: '6px 10px', fontWeight: 700, fontSize: 13, color: '#1a4f6e' }}>{r.urun}</td>
              <TD right bold>{fmt(r.hasat_alani, 'da')}</TD>
              <TD right bold>{fmt(r.uretim_kg, 'kg')}</TD>
              <TD right bold>{fmt(r.verim_kg_da, 'kg/da')}</TD>
            </tr>
          );
          if (r._urun) return (
            <tr key={i} style={{ background: '#f0f4f8' }}>
              <td colSpan={5} style={{ padding: '7px 10px', fontWeight: 700, fontSize: 13, color: '#1a4f6e', borderTop: '2px solid #c8d8e8' }}>{r.urun}</td>
            </tr>
          );
          if (r._urun_top) return (
            <tr key={i} style={{ background: '#f8fafc' }}>
              <TD bold> </TD>
              <TD bold>↳ {r.isletme}</TD>
              <TD right bold>{fmt(r.hasat_alani, 'da')}</TD>
              <TD right bold>{fmt(r.uretim_kg, 'kg')}</TD>
              <TD right bold>{fmt(r.verim_kg_da, 'kg/da')}</TD>
            </tr>
          );
          return (
            <tr key={i}>
              <TD> </TD>
              <td style={{ padding: '5px 10px 5px 22px', fontSize: 12, color: '#555', borderBottom: '1px solid #f0f0f0' }}>{r.isletme}</td>
              <TD right>{fmt(r.hasat_alani, 'da')}</TD>
              <TD right>{fmt(r.uretim_kg, 'kg')}</TD>
              <TD right>{fmt(r.verim_kg_da, 'kg/da')}</TD>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function TarlaUrunDetayTablosu({ data }: { data: any }) {
  const y1: number = data.yil1;
  const y2: number = data.yil2;
  const y3: number = data.yil3;
  const yillar = [y1, y2, y3];

  const thY: React.CSSProperties = {
    padding: '6px 8px', background: '#1a4f6e', color: '#fff',
    fontWeight: 700, fontSize: 12, textAlign: 'center',
    borderRight: '1px solid #2a6090', whiteSpace: 'nowrap',
  };
  const thSub: React.CSSProperties = {
    padding: '5px 8px', background: '#1e5c80', color: '#cde',
    fontWeight: 600, fontSize: 11, textAlign: 'center',
    borderRight: '1px solid #2a6090', whiteSpace: 'nowrap',
  };

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
      <thead>
        <tr>
          <th style={{ ...thY, textAlign: 'left', minWidth: 140 }} rowSpan={2}>İşletme</th>
          <th style={{ ...thY, minWidth: 60 }} rowSpan={2}>Ekim Cinsi</th>
          {yillar.map(y => (
            <th key={y} style={thY} colSpan={3}>{y}</th>
          ))}
        </tr>
        <tr>
          {yillar.map(y => (
            <React.Fragment key={y}>
              <th style={thSub}>Hasat Alanı (da)</th>
              <th style={thSub}>Üretim (ton)</th>
              <th style={thSub}>Verim (kg/da)</th>
            </React.Fragment>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.satirlar.map((r: any, i: number) => {
          const isTop     = r._toplam;
          const isGTop    = r._gtoplam;
          const isITop    = r._isletme_top;
          const isIlk     = r._ilk;
          const isSatir   = r._satir;

          let bg = '#fff';
          if (isTop || isGTop) bg = '#eaf1f8';
          else if (isITop)     bg = '#f5f8fc';
          else if (isIlk)      bg = '#fafcfe';

          const bold = isTop || isGTop || isITop;
          const cText: React.CSSProperties = {
            padding: '5px 8px', borderBottom: '1px solid #eee',
            fontWeight: bold ? 700 : 400,
            color: (isTop || isGTop) ? '#1a4f6e' : '#333',
          };
          const cNum: React.CSSProperties = {
            ...cText, textAlign: 'right',
          };

          return (
            <tr key={i} style={{ background: bg }}>
              <td style={{ ...cText, borderLeft: isIlk ? '3px solid #1a4f6e' : (isSatir ? '3px solid #d8e8f5' : (isITop ? '3px solid #b0c8e0' : (isGTop ? '3px solid #1a4f6e' : undefined))) }}>
                {r.isletme}
              </td>
              <td style={{ ...cText, color: isSatir ? '#666' : (isITop ? '#1a4f6e' : cText.color), paddingLeft: (isSatir || isITop) ? 16 : 8 }}>
                {r.ekim_cinsi}
              </td>
              {yillar.map(y => (
                <React.Fragment key={y}>
                  <td style={cNum}>{fmt(r[`ha_${y}`], 'da')}</td>
                  <td style={cNum}>{fmt(r[`ton_${y}`], 'ton')}</td>
                  <td style={cNum}>{fmt(r[`verim_${y}`], 'kg/da')}</td>
                </React.Fragment>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Ana bileşen ─────────────────────────────────────────────────────────────

export default function FaaliyetRaporTablolari() {
  const simdikiYil = new Date().getFullYear();
  const [yillar, setYillar] = useState<number[]>([simdikiYil - 1, simdikiYil]);
  const [secilenYil, setSecilenYil] = useState(simdikiYil - 1);
  const [secilenRapor, setSecilenRapor] = useState<string | null>(null);
  const [acikGruplar, setAcikGruplar] = useState<string[]>(['fiziksel', 'hayvancilik', 'bitkisel']);
  const [data, setData] = useState<any>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);

  useEffect(() => {
    axios.get(`${API}/api/faaliyet-rapor/yillar`, { headers: hdr() })
      .then(r => {
        const yl: number[] = r.data.yillar;
        if (yl.length) {
          setYillar(yl);
          setSecilenYil(yl[0]);
        }
      })
      .catch(() => {});
  }, []);

  const yukleRapor = useCallback((raporId: string, yil: number) => {
    setYukleniyor(true);
    setHata(null);
    setData(null);
    const rapor = GRUPLAR.flatMap(g => g.raporlar).find(r => r.id === raporId) as any;
    let url = '';
    if (rapor?.urun) {
      url = `${API}/api/faaliyet-rapor/tarla-urun-detay?urun=${encodeURIComponent(rapor.urun)}&yil=${yil}`;
    } else if (raporId === 'traktor') {
      url = `${API}/api/faaliyet-rapor/traktor?yil1=${yil - 1}&yil2=${yil}`;
    } else {
      url = `${API}/api/faaliyet-rapor/${raporId}?yil=${yil}`;
    }
    axios.get(url, { headers: hdr() })
      .then(r => { setData(r.data); setYukleniyor(false); })
      .catch(e => {
        setHata(e.response?.data?.detail || 'Veri yüklenemedi');
        setYukleniyor(false);
      });
  }, []);

  const handleRaporSec = (raporId: string) => {
    setSecilenRapor(raporId);
    yukleRapor(raporId, secilenYil);
  };

  const handleYilDegis = (yil: number) => {
    setSecilenYil(yil);
    if (secilenRapor) yukleRapor(secilenRapor, yil);
  };

  const toggleGrup = (id: string) => {
    setAcikGruplar(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const aktifRapor = GRUPLAR.flatMap(g => g.raporlar).find(r => r.id === secilenRapor);

  const handleYazdir = () => window.print();

  const handleIndir = () => {
    if (!data || !secilenRapor) return;
    const rows: string[][] = [];
    const satirlar = data.satirlar || [];
    if (!satirlar.length) return;
    const cols = Object.keys(satirlar[0]).filter(k => !k.startsWith('_'));
    rows.push(cols);
    satirlar.forEach((r: any) => rows.push(cols.map(c => String(r[c] ?? ''))));
    const csv = rows.map(r => r.join('\t')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/tab-separated-values;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${aktifRapor?.ad || 'rapor'}_${secilenYil}.tsv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', overflow: 'hidden', background: '#f4f6f9' }}>

      {/* ── Sol Sidebar ── */}
      <div style={{
        width: 260, minWidth: 260, background: '#fff', borderRight: '1px solid #e0e6ed',
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
      }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #e0e6ed', background: '#1a4f6e' }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>📊 Faaliyet Raporu</div>
          <div style={{ color: '#a8c8e0', fontSize: 12, marginTop: 4 }}>Tabloları Görüntüle</div>
        </div>

        {/* Yıl seçici */}
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #e0e6ed', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#666' }}>Yıl:</span>
          <select
            value={secilenYil}
            onChange={e => handleYilDegis(Number(e.target.value))}
            style={{ flex: 1, padding: '4px 6px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13 }}
          >
            {yillar.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        {/* Rapor listesi */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {GRUPLAR.map(grup => (
            <div key={grup.id}>
              {/* Grup başlığı */}
              <div
                onClick={() => toggleGrup(grup.id)}
                style={{
                  padding: '9px 14px', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'space-between',
                  background: '#f8fafc', borderBottom: '1px solid #e8edf2',
                  fontSize: 13, fontWeight: 600, color: '#1a4f6e',
                  userSelect: 'none',
                }}
              >
                <span>{grup.ikon} {grup.ad}</span>
                <span style={{ fontSize: 11, color: '#999' }}>{acikGruplar.includes(grup.id) ? '▲' : '▼'}</span>
              </div>
              {/* Rapor öğeleri */}
              {acikGruplar.includes(grup.id) && grup.raporlar.map(r => (
                <div
                  key={r.id}
                  onClick={() => handleRaporSec(r.id)}
                  style={{
                    padding: '8px 14px 8px 28px', cursor: 'pointer',
                    fontSize: 13, color: secilenRapor === r.id ? '#1a4f6e' : '#444',
                    background: secilenRapor === r.id ? '#e8f0f8' : '#fff',
                    borderBottom: '1px solid #f0f3f6',
                    borderLeft: secilenRapor === r.id ? '3px solid #1a4f6e' : '3px solid transparent',
                    fontWeight: secilenRapor === r.id ? 600 : 400,
                    transition: 'all .1s',
                  }}
                >
                  {r.ikon} {r.ad}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── Sağ Panel ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Başlık + butonlar */}
        {secilenRapor && aktifRapor && (
          <div style={{
            padding: '12px 20px', background: '#fff', borderBottom: '1px solid #e0e6ed',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div>
              <span style={{ fontWeight: 700, fontSize: 16, color: '#1a4f6e' }}>
                {aktifRapor.ikon} {aktifRapor.ad}
              </span>
              <span style={{ marginLeft: 12, fontSize: 13, color: '#888' }}>{secilenYil} Yılı</span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleIndir}
                style={{
                  padding: '6px 14px', background: '#fff', border: '1px solid #1a4f6e',
                  color: '#1a4f6e', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >
                ⬇ İndir (Excel)
              </button>
              <button
                onClick={handleYazdir}
                style={{
                  padding: '6px 14px', background: '#1a4f6e', border: 'none',
                  color: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >
                🖨️ Yazdır
              </button>
            </div>
          </div>
        )}

        {/* İçerik alanı */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }} className="print-area">
          {!secilenRapor && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%', flexDirection: 'column', gap: 12, color: '#aaa',
            }}>
              <span style={{ fontSize: 56 }}>📊</span>
              <p style={{ fontSize: 15 }}>Sol listeden bir rapor seçin</p>
            </div>
          )}

          {yukleniyor && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, gap: 12, color: '#666' }}>
              <div style={{ width: 28, height: 28, border: '3px solid #e0e0e0', borderTop: '3px solid #1a4f6e', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
              Yükleniyor…
            </div>
          )}

          {hata && (
            <div style={{ background: '#fdf2f2', border: '1px solid #f5c6cb', borderRadius: 8, padding: 16, color: '#b00' }}>
              ⚠ {hata}
            </div>
          )}

          {!yukleniyor && !hata && data && secilenRapor && (
            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,.07)', overflow: 'auto' }}>
              {secilenRapor === 'arazi'    && <AraziTablosu data={data} />}
              {secilenRapor === 'sulama'   && <SulamaTablosu data={data} />}
              {secilenRapor === 'tesis'    && <TesisTablosu data={data} />}
              {secilenRapor === 'tesis2'   && <Tesis2Tablosu data={data} />}
              {secilenRapor === 'traktor'  && <TraktorTablosu data={data} yil1={secilenYil - 1} yil2={secilenYil} />}
              {secilenRapor === 'tasit'    && <TasitTablosu data={data} />}
              {secilenRapor === 'buyukbas'     && <BuyukbasTablosu data={data} />}
              {secilenRapor === 'buyukbas-irk' && <BuyukbasIrkTablosu data={data} />}
              {secilenRapor === 'kucukbas' && <KucukbasTablosu data={data} />}
              {secilenRapor === 'tarla'        && <TarlaBitkisiTablosu data={data} />}
              {secilenRapor === 'tarla-urun'   && <TarlaUrunTablosu data={data} />}
              {['tarla-bugday','tarla-arpa','tarla-tritikale','tarla-yulaf',
                'tarla-misir','tarla-aycicegi','tarla-mercimek','tarla-nohut'].includes(secilenRapor!) &&
                <TarlaUrunDetayTablosu data={data} />}
              {secilenRapor === 'yem'          && <TarlaBitkisiTablosu data={data} />}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @media print {
          body > * { display: none !important; }
          .print-area { display: block !important; position: fixed; top: 0; left: 0; width: 100%; }
        }
      `}</style>
    </div>
  );
}
